import globalSettingsBranches from '../../../../../../stores/global-settings-branches';
import React, { useEffect, useState } from 'react';
import { ClassMetadata } from '../../../../../../api/types/settings/branches/buildings/classes/class-metadata';
import { observer } from 'mobx-react-lite';
import { Dialog } from 'primereact/dialog';
import { AddNewClassDialog } from './componenets/add-new-class-dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import dateUtils from '../../../../../../utils/date-format';
import './settings-branches-buildings-classes.css';
import { useNavigate, useParams } from 'react-router-dom';
import appApi from '../../../../../../api/app-api';
import { EditTextDialog } from '../../../../componenets/edit-entity-name-dialog/edit-text-dialog';
import { deleteClassOperation } from './operations/delete-class-operation';

export const SettingsBranchesBuildingsClasses = observer(() => {
  const navigate = useNavigate();
  const { branchId, buildingId } = useParams<{
    branchId: string;
    buildingId: string;
  }>();
  const [classes, setClasses] = useState<ClassMetadata[]>();
  const [showDialog, setShowDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editClass, setEditClass] = useState<ClassMetadata | null>(null);
  const [deleteClassId, setDeleteClassId] = useState<number | null>(null);

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        // Fetch branches if they're not already loaded
        if (globalSettingsBranches.branches.length === 0) {
          const branchesData = await appApi.getBranches();
          globalSettingsBranches.branches = branchesData;
        }

        // Find the branch index from the URL parameter
        const branchIndex = globalSettingsBranches.branches.findIndex(
          (branch) => branch.id === Number(branchId),
        );

        // Set the current branch index if found
        if (branchIndex !== -1) {
          globalSettingsBranches.currentBranchIndex = branchIndex;
          const currentBranch = globalSettingsBranches.getCurrentBranch();

          if (currentBranch) {
            // Find the building index
            const buildingIndex = currentBranch.buildings.findIndex(
              (building) => building.id === Number(buildingId),
            );

            if (buildingIndex !== -1) {
              globalSettingsBranches.handleNavigationToClasses(buildingIndex);
              const currentBuilding =
                globalSettingsBranches.getCurrentBuilding();
              if (currentBuilding) {
                setClasses(currentBuilding.classes);
              }
            } else {
              // If building not found, navigate back to buildings list
              navigate(
                `/settings/branch-and-classes/branches/${branchId}/buildings`,
              );
            }
          }
        } else {
          // If branch not found, navigate back to branches list
          navigate('/settings/branch-and-classes/branches');
        }
      } catch (error) {
        console.error('Error initializing classes data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [branchId, buildingId, navigate, globalSettingsBranches.branches]);

  const dialogHide = () => {
    setShowDialog(false);
  };

  const dialogDisplay = () => {
    setShowDialog(true);
  };

  const handleEditClass = (classData: ClassMetadata) => {
    setEditClass(classData);
  };

  const handleSaveClass = async (id: number, newName: string) => {
    try {
      setIsLoading(true);
      const res = await appApi.editClassName(
        Number(branchId),
        Number(buildingId),
        id,
        newName,
      );
      if (res.data.ok) {
        const branchesData = await appApi.getBranches();
        globalSettingsBranches.branches = branchesData;
        setEditClass(null);
      }
    } catch (error) {
      console.error('Error updating class name:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClass = async (classId: number) => {
    await deleteClassOperation(
      Number(branchId),
      Number(buildingId),
      classId,
      setDeleteClassId,
    );
  };

  const onRowSelect = (event: { value: ClassMetadata }) => {
    const classIndex = classes?.findIndex((cls) => cls.id === event.value.id);
    if (classIndex !== undefined && classIndex !== -1) {
      globalSettingsBranches.handleNavigationToClassesAttributes(classIndex);
    }
  };

  return (
    <div>
      <Button
        style={{ height: '30px', marginBottom: '16px' }}
        label={'הוספת כיתה'}
        onClick={dialogDisplay}
      />

      <DataTable
        value={classes}
        loading={isLoading}
        rows={10}
        selectionMode="single"
        onSelectionChange={(e) => onRowSelect(e)}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        className="p-datatable-striped"
      >
        <Column
          style={{ textAlign: 'right' }}
          field="className"
          header="שם"
          sortable
        />
        <Column
          style={{ textAlign: 'right' }}
          field="createdAt"
          header="תאריך יצירה"
          sortable
          body={(rowData: ClassMetadata) =>
            dateUtils.displayFormatDate(new Date(rowData.createdAt))
          }
        />
        <Column
          style={{ textAlign: 'right' }}
          field="createdByName"
          header="נוצר ע״י"
          sortable
        />
        <Column
          style={{ textAlign: 'right' }}
          field="פעולות"
          header=""
          body={(data: ClassMetadata) => (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                icon="pi pi-pencil"
                text
                severity="warning"
                tooltip={'עריכת כיתה'}
                onClick={() => handleEditClass(data)}
              />
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                tooltip={'מחיקת כיתה'}
                loading={deleteClassId === data.id}
                disabled={deleteClassId !== null}
                onClick={() => handleDeleteClass(data.id)}
              />
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header="הוספת כיתה"
        visible={showDialog}
        onHide={dialogHide}
        style={{
          width: '500px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
      >
        <AddNewClassDialog
          closeDialog={dialogHide}
          branchId={Number(branchId)}
          buildingId={Number(buildingId)}
        />
      </Dialog>

      <EditTextDialog
        id={editClass?.id}
        value={editClass?.className}
        title="עריכת כיתה"
        label="שם הכיתה"
        loading={isLoading}
        onSave={handleSaveClass}
        onHide={() => setEditClass(null)}
      />
    </div>
  );
});
